import { ReactNode } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import format from 'date-fns/format';

import { SupportRequestItem } from '../schema';
import { TicketIdBadge } from 'screens/TechExpress/TechExpress.styles';

export const tableHeaders: ColumnDef<SupportRequestItem>[] = [
  { id: 'id', header: 'ID', accessorKey: 'id', enableSorting: false },
  {
    id: 'case_number',
    header: 'Ticket #',
    accessorKey: 'case_number',
    enableSorting: false,
    cell: (row) => <TicketIdBadge>{row.getValue() as ReactNode}</TicketIdBadge>,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    enableSorting: true,
  },
  {
    id: 'assigned_to',
    header: 'Assigned to',
    accessorKey: 'assigned_to',
    enableSorting: false,
  },
  {
    id: 'account_name',
    header: 'Account name',
    accessorKey: 'account_name',
    enableSorting: false,
  },
  {
    id: 'escalated_request',
    header: 'Escalated',
    accessorKey: 'escalated_request',
    enableSorting: false,
  },
  {
    id: 'premier_rep',
    header: 'Premier rep',
    accessorKey: 'premier_rep',
    enableSorting: false,
  },
  {
    id: 'premier_team',
    header: 'Premier team',
    accessorKey: 'premier_team',
    enableSorting: false,
  },
  {
    id: 'ticket_type',
    header: 'Ticket type',
    accessorKey: 'ticket_type',
    enableSorting: true,
  },
  {
    id: 'subject',
    header: 'Subject',
    accessorKey: 'subject',
    enableSorting: false,
  },
  {
    id: 'issue',
    header: 'Issue',
    accessorKey: 'issue',
    enableSorting: false,
  },
  {
    id: 'sub_reason',
    header: 'Sub reason',
    accessorKey: 'sub_reason',
    enableSorting: false,
  },
  {
    id: 'disputed_amount',
    header: 'Disputed amount',
    accessorKey: 'disputed_amount',
    enableSorting: false,
  },
  {
    id: 'comments',
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    id: 'latest_case_comment',
    header: 'Latest comment',
    accessorKey: 'latest_case_comment',
    enableSorting: false,
  },
  {
    id: 'additional_emails',
    header: 'Additional emails',
    accessorKey: 'additional_emails',
    enableSorting: false,
  },
  {
    id: 'supplied_email',
    header: 'Supplied email',
    accessorKey: 'supplied_email',
    enableSorting: false,
  },
  {
    id: 'supplied_name',
    header: 'Supplied name',
    accessorKey: 'supplied_name',
    enableSorting: false,
  },
  {
    id: 'description',
    header: 'Description',
    accessorKey: 'description',
    enableSorting: false,
  },
  {
    id: 'source',
    header: 'Source',
    accessorKey: 'source',
    enableSorting: false,
    cell: (row) => {
      const value = row.getValue();
      if (value === 'G360Web') {
        return 'G360';
      }
      return value;
    },
  },
  {
    id: 'priority',
    header: 'Priority',
    accessorKey: 'priority',
    enableSorting: false,
  },
  {
    id: 'billing_account_id',
    header: 'Billing account ID',
    accessorKey: 'billing_account_id',
    enableSorting: false,
  },
  {
    id: 'service_account_id',
    header: 'Service account ID',
    accessorKey: 'service_account_id',
    enableSorting: false,
  },
  {
    id: 'opened_by',
    header: 'Submitted by',
    accessorKey: 'opened_by',
    enableSorting: true,
  },
  {
    id: 'opened',
    header: 'Opened',
    accessorKey: 'opened',
    enableSorting: true,
    cell: (row) => {
      const value = row.getValue();
      return typeof value === 'string'
        ? format(new Date(value), 'MM/dd/yyyy')
        : '';
    },
  },
  {
    id: 'last_updated',
    header: 'Last updated',
    accessorKey: 'last_updated',
    enableSorting: true,
    cell: (row) => {
      const value = row.getValue();
      return typeof value === 'string'
        ? format(new Date(value), 'MM/dd/yyyy')
        : '';
    },
  },
  {
    id: 'last_modified_by',
    header: 'Last modified by',
    accessorKey: 'last_modified_by',
    enableSorting: false,
  },
];

export const defaultPreferences = [
  { field: 'case_number', enabled: true }, // "1265574",
  { field: 'status', enabled: true }, // "Open",
  { field: 'ticket_type', enabled: true }, // "New Customer Request"
  { field: 'subject', enabled: false }, // "test",
  { field: 'issue', enabled: true }, // null,
  { field: 'opened_by', enabled: true }, // "G360 Salesforce",
  { field: 'opened', enabled: true }, // "Wed, 13 Aug 2025 17:59:09 GMT",
  { field: 'last_updated', enabled: true }, // "Wed, 13 Aug 2025 17:59:13 GMT",
  { field: 'source', enabled: true }, // "G360Web",

  { field: 'account_name', enabled: false }, // "Valvoline",
  { field: 'additional_emails', enabled: false }, // "skulakovskyi@granitenet.com",
  { field: 'assigned_to', enabled: false }, // null,
  { field: 'billing_account_id', enabled: false }, // null,
  { field: 'comments', enabled: false }, // null,
  { field: 'description', enabled: false }, // "test",
  { field: 'disputed_amount', enabled: false }, // null,
  { field: 'escalated_request', enabled: false }, // false,
  { field: 'id', enabled: false }, // "500bf00000GWUmZAAX",
  { field: 'last_modified_by', enabled: false }, // "G360 Salesforce",
  { field: 'latest_case_comment', enabled: false }, // null,
  { field: 'premier_rep', enabled: false }, // "Drew Mullin",
  { field: 'premier_team', enabled: false }, // "Premier 18-Raad",
  { field: 'priority', enabled: false }, // "Low",
  { field: 'service_account_id', enabled: false }, // null,
  { field: 'sub_reason', enabled: false }, // null,
  { field: 'supplied_email', enabled: false }, // "skulakovskyi@granitenet.com",
  { field: 'supplied_name', enabled: false }, // null,
];
